'use client';

import { type Lead, LeadsApi } from '@up2stay/up2promo-api-client';
import {
  Button,
  Checkbox,
  Input,
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
} from '@up2stay/up2promo-ui';
import { useQuery } from '@tanstack/react-query';
import { FileUp, Users } from 'lucide-react';
import { useId, useRef, useState } from 'react';

import { FeedbackRecipientImportPreview } from '@/core/components/feedback/feedback-recipient-import-preview';
import { useFeedbackRecipientImport } from '@/core/components/feedback/use-feedback-recipient-import';
import { downloadCsvFile } from '@/core/lib/download-csv';
import {
  buildFeedbackRecipientExampleCsv,
  FEEDBACK_RECIPIENT_EXAMPLE_CSV_FILENAME,
  isValidFeedbackEmail,
  parseFeedbackRecipientCsv,
  type FeedbackRecipient,
} from '@/core/lib/parse-feedback-recipients';

type ContactOption = {
  email: string;
  name?: string | null;
  feedbackMailOptOut?: boolean;
};

type FeedbackComposeRecipientSourcesProps = {
  workspaceId: string | undefined;
  recipients: FeedbackRecipient[];
  onRecipientsChange: (recipients: FeedbackRecipient[]) => void;
  remainingSlots: number | null;
  contacts: ContactOption[];
  labels: {
    fromLeads: string;
    fromLeadsTitle: string;
    fromLeadsSearch: string;
    fromLeadsEmpty: string;
    fromLeadsAdd: string;
    fromLeadsAddSelected: string;
    fromContacts: string;
    fromCsv: string;
    fromCsvHint: string;
    fromCsvExample: string;
    importPreviewTitle: string;
    importPreviewConfirm: string;
    importPreviewCancel: string;
    importPreviewReady: string;
    importPreviewInvalid: string;
    importPreviewDuplicate: string;
    importPreviewOverQuota: string;
    noneReady: string;
  };
  onImported: (added: number) => void;
};

function leadDisplayName(lead: Lead): string {
  const name = `${lead.firstName ?? ''} ${lead.lastName ?? ''}`.trim();
  return name || lead.email;
}

export function FeedbackComposeRecipientSources({
  workspaceId,
  recipients,
  onRecipientsChange,
  remainingSlots,
  contacts,
  labels,
  onImported,
}: FeedbackComposeRecipientSourcesProps) {
  const fileInputId = useId();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [leadsOpen, setLeadsOpen] = useState(false);
  const [leadSearch, setLeadSearch] = useState('');
  const [selectedLeadIds, setSelectedLeadIds] = useState<Set<string>>(
    () => new Set(),
  );
  const importFlow = useFeedbackRecipientImport({
    recipients,
    onRecipientsChange,
    remainingSlots,
    onImported,
    noneReadyMessage: labels.noneReady,
  });

  const availableContacts = contacts.filter(
    (contact) =>
      !contact.feedbackMailOptOut && isValidFeedbackEmail(contact.email),
  );

  const leadsQuery = useQuery({
    queryKey: ['feedback', workspaceId, 'lead-recipients', leadSearch],
    queryFn: async () => {
      const res = await LeadsApi.find({
        path: { workspaceId: workspaceId as string },
        query: {
          q: leadSearch || undefined,
          limit: 20,
        },
      });
      return (res.data?.items ?? []).filter((lead) =>
        isValidFeedbackEmail(lead.email),
      );
    },
    enabled: Boolean(workspaceId) && leadsOpen,
    staleTime: 15_000,
    retry: false,
  });

  const leadRows = leadsQuery.data ?? [];

  const toggleLead = (leadId: string) => {
    setSelectedLeadIds((current) => {
      const next = new Set(current);
      if (next.has(leadId)) {
        next.delete(leadId);
      } else {
        next.add(leadId);
      }
      return next;
    });
  };

  const handleCsv = async (file: File | undefined) => {
    if (!file) return;
    const text = await file.text();
    importFlow.requestImport(parseFeedbackRecipientCsv(text));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const downloadExampleCsv = () => {
    void downloadCsvFile(
      buildFeedbackRecipientExampleCsv(),
      FEEDBACK_RECIPIENT_EXAMPLE_CSV_FILENAME,
    );
  };

  const addSelectedLeads = () => {
    const incoming = leadRows
      .filter((lead) => selectedLeadIds.has(lead.id))
      .map((lead) => ({
        email: lead.email,
        name: leadDisplayName(lead),
      }));
    importFlow.requestImport(incoming, { skipCleanReview: true });
    setSelectedLeadIds(new Set());
    setLeadsOpen(false);
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setLeadsOpen(true)}
        >
          <Users className="size-4" />
          {labels.fromLeads}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => fileInputRef.current?.click()}
        >
          <FileUp className="size-4" />
          {labels.fromCsv}
        </Button>
        <input
          id={fileInputId}
          ref={fileInputRef}
          type="file"
          accept=".csv,text/csv,text/plain"
          // The visible button above opens this picker; keep the native
          // input out of the tab order and accessibility tree.
          tabIndex={-1}
          aria-hidden
          className="sr-only"
          onChange={(event) => {
            void handleCsv(event.target.files?.[0]);
          }}
        />
      </div>
      <div className="space-y-1">
        <p className="text-xs text-muted-foreground">{labels.fromCsvHint}</p>
        <Button
          type="button"
          variant="dim"
          size="sm"
          className="h-auto px-0 text-xs"
          onClick={downloadExampleCsv}
        >
          {labels.fromCsvExample}
        </Button>
      </div>

      {availableContacts.length > 0 ? (
        <div className="space-y-1.5">
          <p className="text-xs font-medium text-muted-foreground">
            {labels.fromContacts}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {availableContacts.slice(0, 12).map((contact) => (
              <Button
                key={contact.email}
                type="button"
                variant="outline"
                size="sm"
                className="h-auto max-w-full px-2 py-1 text-xs"
                onClick={() =>
                  importFlow.requestImport([
                    {
                      email: contact.email,
                      name: contact.name?.trim() || undefined,
                    },
                  ])
                }
              >
                <span className="truncate">
                  {contact.name?.trim()
                    ? `${contact.name.trim()} · ${contact.email}`
                    : contact.email}
                </span>
              </Button>
            ))}
          </div>
        </div>
      ) : null}

      <ResponsiveDialog
        open={leadsOpen}
        onOpenChange={(open) => {
          setLeadsOpen(open);
          if (!open) setSelectedLeadIds(new Set());
        }}
      >
        <ResponsiveDialogContent>
          <ResponsiveDialogHeader>
            <ResponsiveDialogTitle>
              {labels.fromLeadsTitle}
            </ResponsiveDialogTitle>
          </ResponsiveDialogHeader>
          <div className="space-y-3 px-1 pb-2">
            <Input
              value={leadSearch}
              onChange={(event) => setLeadSearch(event.target.value)}
              placeholder={labels.fromLeadsSearch}
            />
            {leadRows.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {labels.fromLeadsEmpty}
              </p>
            ) : (
              <ul className="max-h-72 space-y-1 overflow-y-auto">
                {leadRows.map((lead) => {
                  const checked = selectedLeadIds.has(lead.id);
                  const checkboxId = `lead-recipient-${lead.id}`;
                  return (
                    <li key={lead.id}>
                      <label
                        htmlFor={checkboxId}
                        className="flex cursor-pointer items-start gap-2 rounded-md px-2 py-2 hover:bg-muted/50"
                      >
                        <Checkbox
                          id={checkboxId}
                          checked={checked}
                          onCheckedChange={() => toggleLead(lead.id)}
                          className="mt-0.5"
                        />
                        <span className="min-w-0 truncate text-sm">
                          <span className="font-medium">
                            {leadDisplayName(lead)}
                          </span>
                          <span className="text-muted-foreground">
                            {' '}
                            · {lead.email}
                          </span>
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
            <p className="text-xs text-muted-foreground">
              {labels.fromLeadsAdd}
            </p>
          </div>
          <ResponsiveDialogFooter>
            <Button
              type="button"
              onClick={addSelectedLeads}
              disabled={selectedLeadIds.size === 0}
            >
              {labels.fromLeadsAddSelected.replace(
                '{count}',
                String(selectedLeadIds.size),
              )}
            </Button>
          </ResponsiveDialogFooter>
        </ResponsiveDialogContent>
      </ResponsiveDialog>

      <FeedbackRecipientImportPreview
        preview={importFlow.preview}
        labels={{
          title: labels.importPreviewTitle,
          confirm: labels.importPreviewConfirm,
          cancel: labels.importPreviewCancel,
          ready: labels.importPreviewReady,
          invalid: labels.importPreviewInvalid,
          duplicate: labels.importPreviewDuplicate,
          overQuota: labels.importPreviewOverQuota,
        }}
        onConfirm={importFlow.confirmPreview}
        onCancel={importFlow.cancelPreview}
      />
    </div>
  );
}
